import { AuthPane } from '@/components/auth/auth-pane';
import { LegalFooter } from '@/components/auth/legal-footer';
import { PhoneAuthPanel } from '@/components/auth/phone-auth-panel';
import { SocialAuthRow } from '@/components/auth/social-auth-row';
import { ControlledCheckbox } from '@/components/forms/controlled-checkbox';
import { ControlledInput } from '@/components/forms/controlled-input';
import { Logo } from '@/components/logo';
import { SeoHead } from '@/components/seo-head';
import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/store/authStore';
import api from '@/services/api';
import { getApiErrorMessage } from '@/utils/api-error';
import { Ionicons } from '@expo/vector-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useLocalSearchParams } from 'expo-router';
import { useGoogleOAuth } from '@/hooks/use-google-oauth';
import { useAppleOAuth } from '@/hooks/use-apple-oauth';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Modal, Platform, Pressable, Text, TextInput, View } from 'react-native';
import { z } from 'zod';
import { useSafeAreaColorEffect } from '@/hooks/use-safe-area-color';
import { showToast } from '@/store/toastStore';
import { useThemedHex } from '@/hooks/use-theme-colors';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';

const loginSchema = z.object({
  email: z.string().email('E-mail inválido'),
  password: z.string().min(6, 'Senha deve ter no mínimo 6 caracteres'),
  rememberMe: z.boolean().optional(),
});

type LoginFormData = z.infer<typeof loginSchema>;

const REMEMBERED_EMAIL_KEY = 'login:remembered-email';

const LOGIN_FAILED_MESSAGE = 'E-mail ou senha incorretos. Confira e tente de novo.';
/** Fallback que o `authStore.login` usa quando a API recusa a senha. */
const STORE_LOGIN_FALLBACK = 'Falha no login. Verifique suas credenciais.';

function loginErrorMessage(error: unknown): string {
  const message = getApiErrorMessage(error, LOGIN_FAILED_MESSAGE);
  return message === STORE_LOGIN_FALLBACK ? LOGIN_FAILED_MESSAGE : message;
}

export default function LoginScreen() {
  const th = useThemedHex();
  const { login, loginByUid } = useAuthStore();
  const { registered } = useLocalSearchParams<{ registered?: string }>();

  /** Qual formulário está no lugar: email e senha, ou telefone e código. */
  const [method, setMethod] = useState<'email' | 'phone'>('email');
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { signInWithGoogle: handleGoogleLogin, isGoogleLoading } = useGoogleOAuth();
  const { signInWithApple: handleAppleLogin, isAppleLoading } = useAppleOAuth();
  const isSocialLoading = isGoogleLoading || isAppleLoading;
  const [forgotPasswordVisible, setForgotPasswordVisible] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState<string | null>(null);
  const [devUid, setDevUid] = useState('');
  const [devPanelOpen, setDevPanelOpen] = useState(false);
  const [isUidLoading, setIsUidLoading] = useState(false);

  useSafeAreaColorEffect(th('#fff'));

  const { control, handleSubmit, watch, reset } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
  });

  useEffect(() => {
    if (registered === '1') {
      showToast('Conta criada. Entre com seu email e senha.', 'success');
    }
  }, [registered]);

  useEffect(() => {
    let isMounted = true;

    AsyncStorage.getItem(REMEMBERED_EMAIL_KEY).then((savedEmail) => {
      if (!isMounted || !savedEmail) return;
      reset({
        email: savedEmail,
        password: '',
        rememberMe: true,
      });
    });

    return () => {
      isMounted = false;
    };
  }, [reset]);

  const emailValue = watch('email');

  const handleOpenForgotPassword = () => {
    setForgotPasswordEmail(emailValue || '');
    setForgotPasswordSent(false);
    setForgotPasswordError(null);
    setForgotPasswordVisible(true);
  };

  const handleForgotPasswordSubmit = async () => {
    const email = forgotPasswordEmail.trim();
    if (!email) return;
    try {
      setForgotPasswordLoading(true);
      setForgotPasswordError(null);
      // A API responde igual exista ou não a conta (anti-enumeração).
      await api.post('/auth/forgot-password', { email });
      setForgotPasswordSent(true);
    } catch (err: any) {
      setForgotPasswordError(
        getApiErrorMessage(err, 'Não foi possível enviar o e-mail. Tente de novo.'),
      );
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const onSubmit = async ({ email, password, rememberMe }: LoginFormData) => {
    try {
      setIsLoading(true);
      setLoginError(null);
      await login({ email, password });
      if (rememberMe) {
        await AsyncStorage.setItem(REMEMBERED_EMAIL_KEY, email);
      } else {
        await AsyncStorage.removeItem(REMEMBERED_EMAIL_KEY);
      }
    } catch (error) {
      setLoginError(loginErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleUidLogin = async () => {
    const uid = devUid.trim();
    if (!uid) return;
    try {
      setIsUidLoading(true);
      setLoginError(null);
      await loginByUid(uid);
    } catch (error) {
      setLoginError(loginErrorMessage(error));
    } finally {
      setIsUidLoading(false);
    }
  };

  const overflowClass =
    Platform.OS === 'web' ? 'overflow-visible min-h-[100dvh]' : 'overflow-hidden';
  return (
    <View className={`flex-1 bg-card ${overflowClass}`}>
      <SeoHead title="Entrar · Controlapag" noindex />
      <View className={`flex-1 ${overflowClass}`}>
        <View
          className={`flex-1 pt-4 md:mx-auto md:w-full md:max-w-6xl md:flex-row ${overflowClass}`}
        >
          <View className="hidden md:relative md:flex md:flex-1 md:items-center md:justify-center md:p-12">
            <View className="relative z-10 max-w-md">
              <Logo size={64} color="tint" />

              <ThemedText className="mt-6 font-bold text-2xl leading-tight text-neutral-900">
                Gerencie seus serviços de forma simples
              </ThemedText>

              <ThemedText className="mt-4 text-base leading-relaxed text-neutral-700">
                Conecte-se com clientes, organize pagamentos e controle tudo em um só lugar.
              </ThemedText>

              <View className="mt-8 gap-4">
                <View className="flex-row items-center gap-3">
                  <View className="h-10 w-10 items-center justify-center rounded-full border border-primary/40 bg-primary/20 dark:bg-primary">
                    <Ionicons className="text-primary dark:text-white" name="checkmark" size={20} />
                  </View>
                  <ThemedText className="flex-1 text-neutral-800">
                    Gestão completa de pagamentos
                  </ThemedText>
                </View>

                <View className="flex-row items-center gap-3">
                  <View className="h-10 w-10 items-center justify-center rounded-full border border-primary/40 bg-primary/20 dark:bg-primary">
                    <Ionicons className="text-primary dark:text-white" name="checkmark" size={20} />
                  </View>
                  <ThemedText className="flex-1 text-neutral-800">
                    Controle de clientes e serviços
                  </ThemedText>
                </View>

                <View className="flex-row items-center gap-3">
                  <View className="h-10 w-10 items-center justify-center rounded-full border border-primary/40 bg-primary/20 dark:bg-primary">
                    <Ionicons className="text-primary dark:text-white" name="checkmark" size={20} />
                  </View>
                  <ThemedText className="flex-1 text-neutral-800">
                    Relatórios e estatísticas
                  </ThemedText>
                </View>
              </View>
            </View>
          </View>

          <AuthPane>
            <View className="w-full grow md:max-w-md md:grow-0">
              <View className="flex-1 px-6 pb-6 md:pb-8 md:pt-8">
                <View>
                  <View className="mb-4 items-center">
                    <View className="md:hidden">
                      <Logo size={64} color="tint" />
                    </View>
                    <ThemedText
                      role="heading"
                      aria-level={1}
                      className="mt-2 font-bold text-xl md:mt-0 md:text-2xl"
                    >
                      Bem-vindo de volta
                    </ThemedText>
                    <ThemedText className="mt-1 text-center text-sm text-gray-500">
                      Entre na sua conta para continuar
                    </ThemedText>
                  </View>

                  {method === 'phone' ? (
                    <PhoneAuthPanel />
                  ) : (
                    <View className="gap-3">
                      <ControlledInput
                        control={control}
                        name="email"
                        label="E-mail"
                        placeholder="seu@email.com"
                        keyboardType="email-address"
                        autoCapitalize="none"
                        autoComplete="off"
                        compact
                      />

                      <ControlledInput
                        control={control}
                        name="password"
                        label="Senha"
                        placeholder="••••••••"
                        secureTextEntry
                        autoComplete="off"
                        compact
                      />

                      {loginError && (
                        <View className="mb-2 flex-row items-center gap-2 rounded-xl border border-error-100 bg-error-50 px-4 py-3">
                          <Ionicons className="text-error-600" name="alert-circle" size={18} />
                          <ThemedText className="flex-1 font-medium text-sm text-error-600">
                            {loginError}
                          </ThemedText>
                        </View>
                      )}

                      <View className="mt-1 flex-row items-center justify-between gap-3">
                        <ControlledCheckbox
                          label="Lembrar"
                          control={control}
                          name="rememberMe"
                          labelReverse
                          inline
                        />
                        <Pressable
                          onPress={handleOpenForgotPassword}
                          hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
                          className="z-10 shrink-0 px-1 py-2 active:opacity-70"
                          style={Platform.OS === 'web' ? { cursor: 'pointer' } : undefined}
                        >
                          <Text className="font-semibold text-sm text-primary">
                            Esqueci a senha
                          </Text>
                        </Pressable>
                      </View>
                    </View>
                  )}
                </View>

                <View className="mt-auto gap-3 pt-4">
                  {/* Atalho de desenvolvimento: fechado, ocupa uma linha. */}
                  {__DEV__ && !devPanelOpen && (
                    <Pressable onPress={() => setDevPanelOpen(true)} className="items-center py-1">
                      <ThemedText className="font-semibold text-xs text-amber-700">
                        Dev: login por UID
                      </ThemedText>
                    </Pressable>
                  )}
                  {__DEV__ && devPanelOpen && (
                    <View className="gap-2 rounded-xl border border-amber-200 bg-amber-50/80 p-3">
                      <Pressable onPress={() => setDevPanelOpen(false)}>
                        <ThemedText className="font-semibold text-xs text-amber-800">
                          Dev: login por UID
                        </ThemedText>
                      </Pressable>
                      <TextInput
                        className="rounded-lg border border-amber-300 bg-card px-3 py-2 text-sm text-neutral-900"
                        placeholder="Cole o UUID do usuário"
                        placeholderTextColor="#9ca3af"
                        value={devUid}
                        onChangeText={setDevUid}
                        autoCapitalize="none"
                        autoCorrect={false}
                      />
                      <Button
                        title={isUidLoading ? 'Entrando...' : 'Entrar por UID'}
                        onPress={handleUidLogin}
                        disabled={isUidLoading || !devUid.trim()}
                        size="sm"
                        variant="outline"
                        className="border-amber-400"
                      />
                    </View>
                  )}
                  {method === 'email' && (
                    <Button
                      title={isLoading ? 'Entrando...' : 'Entrar'}
                      onPress={handleSubmit(onSubmit)}
                      disabled={isLoading || isSocialLoading}
                      size="md"
                      className="w-full"
                    />
                  )}

                  <View className="flex-row items-center">
                    <View className="h-[1px] flex-1 bg-neutral-200" />
                    <ThemedText className="mx-4 font-medium text-xs text-neutral-400">
                      ou
                    </ThemedText>
                    <View className="h-[1px] flex-1 bg-neutral-200" />
                  </View>

                  <SocialAuthRow
                    intent="login"
                    mode={method}
                    onWhatsApp={() => setMethod('phone')}
                    onEmail={() => setMethod('email')}
                    onGoogle={handleGoogleLogin}
                    onApple={handleAppleLogin}
                    isGoogleLoading={isGoogleLoading}
                    isAppleLoading={isAppleLoading}
                    disabled={isLoading || isSocialLoading}
                  />

                  <View className="flex-row items-center justify-center">
                    <ThemedText className="text-sm text-gray-500">Não tem uma conta? </ThemedText>
                    <Link href="/register" asChild>
                      <Pressable testID="auth-login-register-link">
                        <ThemedText className="font-bold text-sm text-primary">
                          Cadastre-se
                        </ThemedText>
                      </Pressable>
                    </Link>
                  </View>
                </View>
              </View>
            </View>
          </AuthPane>
        </View>
      </View>

      <LegalFooter action="entrar" />

      <Modal
        visible={forgotPasswordVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setForgotPasswordVisible(false)}
      >
        <KeyboardAvoidingView behavior="padding" style={{ flex: 1 }}>
          <Pressable
            className="flex-1 justify-center bg-black/50 p-4"
            onPress={() => setForgotPasswordVisible(false)}
          >
            <Pressable className="rounded-2xl bg-card p-6" onPress={(e) => e.stopPropagation()}>
              <ThemedText className="mb-1 font-bold text-lg">Esqueci a senha</ThemedText>
              <ThemedText className="mb-4 text-sm text-gray-500">
                Informe seu email e enviaremos um link para redefinir sua senha.
              </ThemedText>
              {forgotPasswordSent ? (
                <>
                  <View className="mb-4 flex-row items-center gap-2 rounded-xl border border-green-200 bg-green-50 px-4 py-3">
                    <Ionicons className="text-success-600" name="checkmark-circle" size={20} />
                    <ThemedText className="flex-1 text-sm text-green-700">
                      Se existir uma conta com este email, você receberá um link para redefinir a
                      senha. Verifique sua caixa de entrada e spam.
                    </ThemedText>
                  </View>
                  <Button
                    title="Fechar"
                    onPress={() => setForgotPasswordVisible(false)}
                    size="md"
                    className="w-full"
                  />
                </>
              ) : (
                <>
                  <TextInput
                    className="mb-4 rounded-xl border border-neutral-200 bg-card px-4 py-3 text-base text-neutral-900"
                    placeholder="seu@email.com"
                    placeholderTextColor="#9ca3af"
                    value={forgotPasswordEmail}
                    onChangeText={setForgotPasswordEmail}
                    keyboardType="email-address"
                    autoCapitalize="none"
                    autoComplete="email"
                    editable={!forgotPasswordLoading}
                  />
                  {forgotPasswordError && (
                    <View className="mb-4 flex-row items-center gap-2 rounded-xl border border-error-200 bg-error-50 px-4 py-3">
                      <Ionicons className="text-error-600" name="alert-circle" size={18} />
                      <ThemedText className="flex-1 text-sm text-error-600">
                        {forgotPasswordError}
                      </ThemedText>
                    </View>
                  )}
                  <View className="flex-row gap-3">
                    <Button
                      title="Cancelar"
                      onPress={() => setForgotPasswordVisible(false)}
                      size="md"
                      variant="outline"
                      className="flex-1"
                    />
                    <Button
                      title={forgotPasswordLoading ? 'Enviando...' : 'Enviar link'}
                      onPress={handleForgotPasswordSubmit}
                      disabled={forgotPasswordLoading || !forgotPasswordEmail.trim()}
                      size="md"
                      className="flex-1"
                    />
                  </View>
                </>
              )}
            </Pressable>
          </Pressable>
        </KeyboardAvoidingView>
      </Modal>
    </View>
  );
}
